import { AlertTriangle, Flag, MessageSquareWarning, FileWarning } from 'lucide-react';
import type { UserDetail } from '../UserDetailPage';

interface Props {
  user: UserDetail;
}

export function UserRiskTab({ user }: Props) {
  const riskColor = (level: string | null) => {
    switch (level) {
      case 'CRITICAL': return { bg: 'bg-red-900/30', border: 'border-red-700/50', text: 'text-red-400', bar: 'bg-red-500' };
      case 'HIGH': return { bg: 'bg-orange-900/30', border: 'border-orange-700/50', text: 'text-orange-400', bar: 'bg-orange-500' };
      case 'MEDIUM': return { bg: 'bg-amber-900/30', border: 'border-amber-700/50', text: 'text-amber-400', bar: 'bg-amber-500' };
      default: return { bg: 'bg-green-900/30', border: 'border-green-700/50', text: 'text-green-400', bar: 'bg-green-500' };
    }
  };

  const colors = riskColor(user.riskLevel);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-white">Risk Assessment & Reports</h2>

      {/* Risk Score */}
      <div className={`${colors.bg} border ${colors.border} rounded-lg p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium text-slate-400">Overall Risk Score</h3>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-4xl font-bold text-white">{user.riskScore ?? 0}</span>
              <span className={`text-sm font-medium px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                {user.riskLevel || 'UNKNOWN'}
              </span>
            </div>
          </div>
          <AlertTriangle className={`h-12 w-12 ${colors.text} opacity-50`} />
        </div>
        <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full ${colors.bar} rounded-full transition-all`} style={{ width: `${Math.min(user.riskScore ?? 0, 100)}%` }} />
        </div>
        <div className="flex justify-between mt-1 text-xs text-slate-500">
          <span>0 - Low</span>
          <span>30 - Medium</span>
          <span>60 - High</span>
          <span>80 - Critical</span>
        </div>
      </div>

      {/* Indicator Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Flag className="h-5 w-5 text-red-400" />}
          label="Reports Against"
          value={user.reportsAgainstCount}
          description="Reports filed against this user"
        />
        <StatCard
          icon={<FileWarning className="h-5 w-5 text-amber-400" />}
          label="Reports Filed"
          value={user.reportsFiledCount}
          description="Reports this user has filed"
        />
        <StatCard
          icon={<MessageSquareWarning className="h-5 w-5 text-orange-400" />}
          label="Flagged Messages"
          value={user.flaggedMessagesCount}
          description="AI-flagged messages from this user"
        />
        <StatCard
          icon={<AlertTriangle className="h-5 w-5 text-purple-400" />}
          label="Warnings"
          value={user.warningsCount}
          description="Warnings issued to this user"
        />
      </div>

      {/* Risk Factors */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Risk Factor Breakdown</h3>
        <div className="space-y-3">
          <RiskFactor
            label="Reports against user"
            points={Math.min(user.reportsAgainstCount * 10, 40)}
            maxPoints={40}
            detail={`${user.reportsAgainstCount} reports x 10 pts (max 40)`}
          />
          <RiskFactor
            label="Flagged messages"
            points={Math.min(user.flaggedMessagesCount * 5, 30)}
            maxPoints={30}
            detail={`${user.flaggedMessagesCount} messages x 5 pts (max 30)`}
          />
          <RiskFactor
            label="Email verification"
            points={user.emailVerified ? 0 : 10}
            maxPoints={10}
            detail={user.emailVerified ? 'Verified (0 pts)' : 'Not verified (+10 pts)'}
          />
          <RiskFactor
            label="Mobile verification"
            points={user.mobileVerified ? 0 : 5}
            maxPoints={5}
            detail={user.mobileVerified ? 'Verified (0 pts)' : 'Not verified (+5 pts)'}
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, description }: { icon: React.ReactNode; label: string; value: number; description: string }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <span className="text-sm font-medium text-slate-400">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <p className="text-xs text-slate-500 mt-1">{description}</p>
    </div>
  );
}

function RiskFactor({ label, points, maxPoints, detail }: { label: string; points: number; maxPoints: number; detail: string }) {
  const pct = maxPoints > 0 ? (points / maxPoints) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-300">{label}</span>
        <span className="text-sm font-medium text-slate-400">+{points} pts</span>
      </div>
      <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${points > 0 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{detail}</p>
    </div>
  );
}
